import React, { useId } from 'react';
import { ChevronDown, Clock, Compass, Users } from 'lucide-react';
import type { TripRole } from '../utils/tripRoles';
import { FIELD, fieldBorder } from './UI';

const ROLES = {
  traveller: {
    label: 'Travel companion',
    description: 'Can contribute to the existing itinerary, including accommodation, transport and shortlists, and log and settle expenses with others.',
    Icon: Users,
  },
  admin: {
    label: 'Trip Admin',
    description: 'Can also add or delete destinations, manage contributions by others, and set trip permissions and preferences.',
    Icon: Compass,
  },
};

export function TripRoleSelect({ value, onChange, canAssignAdmin = true, disabled = false }: {
  value: TripRole; onChange: (role: TripRole) => void; canAssignAdmin?: boolean; disabled?: boolean;
}) {
  const id = useId();
  const role = canAssignAdmin ? value : 'traveller';
  return <div className="space-y-1.5">
    <label htmlFor={id} className="ds-label">Permissions</label>
    <div className="relative">
      <select id={id} value={role} disabled={disabled || !canAssignAdmin}
        aria-describedby={`${id}-description`}
        onChange={event => onChange(event.target.value === 'admin' && canAssignAdmin ? 'admin' : 'traveller')}
        className={`${FIELD} ${fieldBorder(false, true)} appearance-none disabled:cursor-not-allowed`}>
        <option value="traveller">{ROLES.traveller.label}</option>
        {canAssignAdmin && <option value="admin">{ROLES.admin.label}</option>}
      </select>
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 size-4 text-app-sub" aria-hidden="true" />
    </div>
    <p id={`${id}-description`} className="text-sm text-app-sub leading-relaxed">{ROLES[role].description}</p>
  </div>;
}

const BADGE = 'w-fit inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border';

export function TravellerRoleBadges({ role, pending }: { role: TripRole; pending: boolean }) {
  const { label, Icon } = ROLES[role];
  return <div className="flex flex-wrap items-center gap-1 mt-1">
    <span className={`${BADGE} bg-green-100/50 text-green-700 border-green-200`}
      title={pending ? `${label} when the invitation is accepted` : label}>
      <Icon className="size-2.5" aria-hidden="true" />{label}
    </span>
    {pending && <span className={`${BADGE} bg-amber-100/50 text-amber-700 border-amber-200`}>
      <Clock className="size-2.5" aria-hidden="true" />Pending
    </span>}
  </div>;
}
